import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "@constructorfleet/ui/design-system";

export const CHUNK_RELOAD_KEY = "chunk_error_reloaded";

function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const msg = error.message.toLowerCase();
  return (
    (error.name === "TypeError" && msg === "load failed") ||
    msg.includes("importing a module script failed") ||
    msg.includes("failed to fetch dynamically imported module") ||
    msg.includes("error loading dynamically imported module") ||
    msg.includes("dynamically imported module")
  );
}

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  reloadPage?: () => void;
}

interface State {
  errored: boolean;
}

export class ChunkLoadErrorBoundary extends Component<Props, State> {
  state: State = { errored: false };

  static getDerivedStateFromError(): State {
    return { errored: true };
  }

  componentDidCatch(error: Error, _info: ErrorInfo) {
    if (!isChunkLoadError(error)) return;

    const alreadyReloaded = sessionStorage.getItem(CHUNK_RELOAD_KEY);
    if (!alreadyReloaded) {
      sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
      this.reloadPage();
    }
  }

  private reloadPage() {
    (this.props.reloadPage ?? window.location.reload.bind(window.location))();
  }

  render() {
    if (this.state.errored) {
      return (
        this.props.fallback ?? (
          <div className="cf-loading-block">
            <p>Something went wrong loading this page.</p>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => {
                sessionStorage.removeItem(CHUNK_RELOAD_KEY);
                this.reloadPage();
              }}
            >
              Reload
            </Button>
          </div>
        )
      );
    }
    return this.props.children;
  }
}
